import { api } from 'roadie-ui'
import * as Sentry from '@sentry/react'
import { ZodType } from 'zod'
import { getClusterServiceUrl } from './cluster'
import { getSessionInfo } from './session'

interface ApiConfig {
  baseUrlEnvVar?: string
  serviceName?: string
  authHeaderName?: string
  authHeaderValuePrefix?: string
}

/**
 * Represents the RoadieAPI type that's defined in roadie-ui but not exported.
 */
export type RoadieAPI = typeof api

export interface ApiResponse<T> {
  data: T | null
  error: string | null
  status: number
  success: boolean
}

export type ExternalAPI = ReturnType<typeof createExternalApi>

async function handleApiGetResponse<T>(
  responsePromise: Promise<any>,
  path: string,
  validationSchema: ZodType<unknown>,
  deserializer: (data: unknown) => T,
  params?: Record<string, string | number | boolean>
): Promise<ApiResponse<T>> {
  try {
    const response = await responsePromise
    const validationResult = validationSchema.safeParse(response.data)

    if (!validationResult.success) {
      const validationError = validationResult.error
      const sentryErrorTitle = new Error(`GET ${path} API response validation failed`)

      Sentry.captureException(sentryErrorTitle, {
        level: 'warning',
        tags: {
          error_type: 'api_validation_failure',
          endpoint: path,
        },
        extra: {
          httpStatus: response.rawResponse.status,
          // TODO: Figure out why Sentry can't serialize the "data" field in here correctly
          // for output (without calling JSON.stringify on it)
          validationIssues: JSON.stringify(validationError.issues, null, 2),
          // TODO: Figure out why Sentry can't serialize the "path" field in here correctly
          // for output (without calling JSON.stringify on it)
          responseData: JSON.stringify(response.data, null, 2),
        },
      })

      // Deserialize the data (deserializer may provide defaults for missing fields)
      // but return an error (and success as "false") so callers know validation failed
      return {
        data: deserializer(response.data),
        error: 'API response validation failed',
        status: response.rawResponse.status || 200,
        success: false,
      }
    }

    return {
      data: deserializer(response.data),
      error: null,
      status: response.rawResponse.status || 200,
      success: true,
    }
  } catch (err: any) {
    Sentry.captureException(new Error(`GET ${path} API call failed`), {
      level: 'error',
      tags: {
        error_type: 'api_call_failure',
        endpoint: path,
      },
      extra: {
        error: err.message,
        status: err.response?.rawResponse?.status,
        params,
        responseData: err.response?.data,
      },
    })

    return {
      data: null,
      error: err.message || 'An error occurred while fetching data',
      status: err.response?.rawResponse?.status || 500,
      success: false,
    }
  }
}

export async function apiGetExternal<T>(
  api: ExternalAPI,
  path: string,
  validationSchema: ZodType<unknown>,
  deserializer: (data: unknown) => T,
  params?: Record<string, string | number | boolean>
): Promise<ApiResponse<T>> {
  return handleApiGetResponse(
    api.get(path, params ? { params } : undefined),
    path,
    validationSchema,
    deserializer,
    params
  )
}

export async function apiGet<T>(
  api: RoadieAPI,
  version: number,
  path: string,
  validationSchema: ZodType<unknown>,
  deserializer: (data: unknown) => T,
  params?: Record<string, string | number | boolean>
): Promise<ApiResponse<T>> {
  return handleApiGetResponse(
    api.version(version).get(path, params ? { params } : undefined),
    path,
    validationSchema,
    deserializer,
    params
  )
}

function addAuthHeader(config: any = {}, authConfig: Pick<ApiConfig, 'authHeaderName' | 'authHeaderValuePrefix'> = {}) {
  const token = getSessionInfo()?.access_token

  if (!token) {
    Sentry.captureException(new Error('Missing access token when calling API Factory'), {
      level: 'warning',
      tags: {
        error_type: 'missing_auth_token',
      },
    })

    return config
  }

  const headerName = authConfig.authHeaderName ?? 'Authorization'
  const valuePrefix = authConfig.authHeaderValuePrefix ?? 'Bearer '
  return {
    ...config,
    headers: {
      ...(config.headers || {}),
      [headerName]: `${valuePrefix}${token}`,
    },
  }
}

export function createExternalApi(config: ApiConfig) {
  let baseUrl: string | undefined

  function getBaseUrl(): string {
    const resolvedBaseUrl =
      baseUrl ||
      (config.serviceName
        ? getClusterServiceUrl(config.serviceName)
        : config.baseUrlEnvVar
          ? import.meta.env[config.baseUrlEnvVar]
          : undefined)

    if (!resolvedBaseUrl) {
      const configuredService = config.serviceName || config.baseUrlEnvVar || 'external API'
      const error = new Error(`Missing base URL for ${configuredService}`)
      Sentry.captureException(error, {
        level: 'error',
        tags: {
          error_type: 'api_configuration_error',
          service: configuredService,
        },
      })
      throw error
    }

    return resolvedBaseUrl
  }

  function setBaseUrl(url: string) {
    baseUrl = url
  }

  const apiObject = {
    get: (path: string, requestConfig = {}) => {
      return api.external().get(`${getBaseUrl()}${path}`, addAuthHeader(requestConfig, config))
    },
    post: (path: string, data: any, requestConfig = {}) => {
      return api.external().post(`${getBaseUrl()}${path}`, data, addAuthHeader(requestConfig, config))
    },
    patch: (path: string, data: any, requestConfig = {}) => {
      return api.external().patch(`${getBaseUrl()}${path}`, data, addAuthHeader(requestConfig, config))
    },
    delete: (path: string, requestConfig = {}) => {
      return api.external().delete(`${getBaseUrl()}${path}`, addAuthHeader(requestConfig, config))
    },
  }

  return {
    ...apiObject,
    getBaseUrl,
    setBaseUrl,
  }
}
